import {
  ChevronDown,
  ChevronRight,
  LoadingProgress,
  NavigableTypography,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import { paths } from '@issuemanager/apps/client/paths';
import { useAtlasCoreThemeTokens } from '@issuemanager/apps/client/theme';
import {
  Box,
  IconButton,
  IconButtonProps,
  styled,
  useEventCallback,
} from '@mui/material';
import composeClasses from '@mui/utils/composeClasses/composeClasses';
import {
  getDataGridUtilityClass,
  GridRenderCellParams,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MasterIssueRowModel } from '../issue-grid/types';

type StyledBoxProps = {
  depth: number;
};

const StyledBox = styled(Box)<StyledBoxProps>`
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-left: ${({ theme, depth }) =>
    depth === 0 ? 0 : theme.spacing(0.5 + 1.5 * depth)};
  margin-left: ${({ theme, depth }) => (depth === 0 ? 0 : theme.spacing(2))};
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex: 1 0 0;
  & svg {
    display: flex;
    height: 24px;
    width: 24px;
    justify-content: center;
    align-items: center;
  }
`;

export function GroupingCell(
  props: GridRenderCellParams<string, MasterIssueRowModel>,
) {
  const { id, field, rowNode, value, row } = props;
  const apiRef = useGridApiContext();
  const { rowsLoading } = useDataGridContext();
  const navigate = useNavigate();
  const core = useAtlasCoreThemeTokens();
  const rootProps = useGridRootProps();

  const handleRowClick = useCallback((params: GridRenderCellParams) => {
    // NOTE: Only navigate if the row is not an action
    if (params.row.path?.length < 2) {
      navigate(`/${paths.issues.index}/${params.row.code}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChevronClick: IconButtonProps['onClick'] = useEventCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    },
  );

  const classes = composeClasses(
    {
      root: ['treeDataGroupingCell'],
      toggle: ['treeDataGroupingCellToggle'],
    },
    getDataGridUtilityClass,
    rootProps.classes,
  );

  const isParent = !rowNode.parent;
  const hasChildren =
    isParent && rowNode.children && rowNode.children.length > 0;

  const isExpanded = rowNode.childrenExpanded;
  const isLoading = rowsLoading[row.code];

  const icon = useMemo(() => {
    if (isExpanded) {
      if (isLoading) {
        return <LoadingProgress />;
      }

      return (
        <ChevronDown active activeColor={core.color.legacy.blackNear.value} />
      );
    }

    return (
      <ChevronRight active activeColor={core.color.legacy.blackNear.value} />
    );
  }, [isExpanded, isLoading, core.color.legacy.blackNear.value]);

  return (
    <Box className={classes.root} sx={{ ml: rowNode.depth * 2 }}>
      <StyledBox depth={rowNode.depth} className={classes.toggle}>
        {isParent && (
          <>
            {hasChildren && (
              <IconButton
                data-testid='treeDataGroupingCellToggle'
                size='small'
                onClick={handleChevronClick}
                tabIndex={-1}
                aria-label={
                  isExpanded
                    ? apiRef.current.getLocaleText('treeDataCollapse')
                    : apiRef.current.getLocaleText('treeDataExpand')
                }
              >
                {icon}
              </IconButton>
            )}
            <NavigableTypography
              onClick={() => handleRowClick(props)}
              pl={hasChildren ? 0 : 4.5}
            >
              {value}
            </NavigableTypography>
          </>
        )}
      </StyledBox>
    </Box>
  );
}
